'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import PopupTemplate from '@/app/ui/popupTemplate';
import UserBalance from './userBalance';
import { safePathFromCallback } from '../../billing/utils/callbackSecurity';

interface UserBalancePopupProps {
  isOpen: boolean;
  onClose: () => void;
  callbackBaseUrl?: string; // URL to return to after payment completion
}

/**
 * UserBalancePopup component with dynamic callback URL support
 *
 * Usage examples:
 *
 * // For profile page (default behavior)
 * <UserBalancePopup
 *   isOpen={showBalancePopup}
 *   onClose={() => setShowBalancePopup(false)}
 *   callbackBaseUrl={'/profile'}
 * />
 *
 * // For chat page - preserve chat session
 * <UserBalancePopup
 *   isOpen={showBalancePopup}
 *   onClose={() => setShowBalancePopup(false)}
 *   callbackBaseUrl={window.location.pathname + window.location.search}
 * />
 *
 * // For subscription page (no callback needed)
 * <UserBalancePopup
 *   isOpen={showBalancePopup}
 *   onClose={() => setShowBalancePopup(false)}
 * />
 */

/**
 * Utility function to get current URL for callback purposes
 * Use this in chat pages or any page where you want to return after payment
 * This function ensures the returned URL is safe for redirects
 */
export function getCurrentUrlForCallback(): string {
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.pathname + window.location.search;
    // Validate the URL to ensure it's safe
    return safePathFromCallback(currentUrl) || '/';
  }
  return '/';
}

export default function UserBalancePopup({ isOpen, onClose, callbackBaseUrl = '/profile' }: UserBalancePopupProps) {
  if (!isOpen || typeof window === 'undefined') return null;

  return createPortal(
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={true}
      maxWidth="max-w-4xl"
      className="max-h-[90vh] flex flex-col overflow-hidden"
    >
      <UserBalance callbackBaseUrl={callbackBaseUrl} />
    </PopupTemplate>,
    document.body
  );
}
